import React from 'react'
import { Skeleton, Typography, Button, Row, Col, Form, Input, Transfer, Divider, message } from 'antd'
import { FormComponent, BaseFormComponentProps } from '../../BaseComponent'
import IAdminRole from '../../../interfaces/IAdminRole'
import { AdminRoleFindRequest, AdminRoleListRequest, AdminRoleCreateRequest, AdminRoleUpdateRequest }from '../../../services/api/interfaces/ApiRequest'

type AdminRoleProps = BaseFormComponentProps & {
  roleName?: string,
  data?: IAdminRole,
  roles?: IAdminRole[],
  error: any | null,

  loading: boolean,
  history: any,

  loadAdminRole: (args: AdminRoleFindRequest) => void,
  loadAdminRoles: (args: AdminRoleListRequest) => void,

  dispatchCreate: (args: AdminRoleCreateRequest) => void,
  dispatchUpdate: (args: AdminRoleUpdateRequest) => void
}

export default class AdminRole extends FormComponent<AdminRoleProps> {

  state = {
    saving: false,
    loaded: false,
    childRolesSelected: [] as string[]
  }

  componentDidMount() {
    const { roleName, loadAdminRole, loadAdminRoles } = this.props

    // Check permissions
    this.validateAccessPermissions()

    if (roleName) {
      loadAdminRole({
        name: roleName
      })
    } else {
      loadAdminRoles({
        role: 2
      })
    }
  }

  componentDidUpdate(prevProps: AdminRoleProps, prevState) {
    const wasLoading = prevProps.loading && !this.props.loading
    let newState = Object.assign({}, this.state)

    // Check permissions
    this.validateAccessPermissions()

    // We are loaded
    if (!this.state.loaded && wasLoading) {
      // Load child roles
      if (this.props.roleName && !this.props.roles && !this.props.error) {
        this.props.loadAdminRoles({
          role: 2
        })

        return
      }

      newState.loaded = true

      // Set selected cild roles for this parent
      if (this.props.roleName && this.props.data) {
        const { permissions } = this.props.data

        newState.childRolesSelected = permissions?.map(p => p.child) || []
      }
    }

    // We are saving
    if (this.state.saving && wasLoading) {
      // Add the new role name to url
      if (!this.props.roleName && this.props.data) {
        this.props.history.replace(`/admin/role/${this.props.data.name}`)
      }

      if (this.props.error) {
        message.error(`Failed to save role. Name already exists!`)
      } else {
        message.success('Role saved successfully!')
      }

      newState.saving = false
    }

    // Update if changed
    if (newState.loaded !== this.state.loaded || newState.saving !== prevState.saving) {
      this.setState(newState)
    }
  }

  handleChildRoleSelection = (targetKeys: any, direction: any, moveKeys: any) => {
    this.setState({
      childRolesSelected: targetKeys
    })
  }

  handleSave = (values) => {
    const { roleName, data, dispatchUpdate, dispatchCreate } = this.props
    const { childRolesSelected } = this.state

    if (roleName) {
      const removed = data?.permissions.map(p => p.child).filter(child => !childRolesSelected.includes(child)) || []

      dispatchUpdate({
        admin_auth_item: {
          ...values,
          child: childRolesSelected,
          child_deleted: removed
        }
      })
      this.setState({
        saving: true
      })
    } else {
      dispatchCreate({
        admin_auth_item: {
          ...values,
          child: childRolesSelected
        }
      })
      this.setState({
        saving: true
      })
    }
  }

  render() {
    const { roleName, data, roles, loading } = this.props
    const { loaded, childRolesSelected } = this.state

    if (!loaded && loading) {
      return <Skeleton />
    }

    const childRolesOptions = roles?.map(role => {
      return {
        key: role.name,
        title: role.rule_name || role.name,
        description: role.description,
        chosen: childRolesSelected.includes(role.name)
      }
    })

    let values:any = {
      description: ''
    }

    if (roleName) {
      values = {
        ...data || {}
      }
    }

    return (
      <Form
        name="admin_role"
        initialValues={values}
        layout="vertical"
        onFinish={this.handleSave}
      >
        <Row gutter={12}>
          <Col xs={24} sm={12} md={12}>
            <Form.Item
              label="Role Name"
              name="name"
              rules={[
                {
                  required: true,
                  message: 'Missing a name for the role'
                }
              ]}
            >
              <Input readOnly={roleName !== undefined && roleName.length > 0} disabled={roleName !== undefined && roleName.length > 0} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={12}>
            <Form.Item
              label="Role Description"
              name="description"
              rules={[
                {
                  required: true,
                  message: 'Missing a description for the role'
                }
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          </Col>
        </Row>
        
        <Divider />

        <Row>
          <Col xs={24}>
            <Typography.Title level={5}>Permissions</Typography.Title>
            <Transfer
              className="shelter-transfer"
              dataSource={childRolesOptions}
              targetKeys={childRolesSelected}
              render={(item: any) => item.title}
              onChange={this.handleChildRoleSelection}
            />
          </Col>
        </Row>
        
        <Form.Item style={{marginTop: 32}}>
          <Button type="primary" htmlType="submit" size="large" loading={loading} disabled={childRolesSelected.length === 0} ref={(refs) => this.actionRefs.save = refs} block>
            Save
          </Button>
        </Form.Item>
      </Form>
    )
  }
}
